<template>
    <Head title="WorkIQ Data Tools" />
    <!-- Staging-only data-creation tools for the Work IQ playground. Self-contained inline
         sidebar (back to playground + brand + tool nav) so the whole thing deletes as a unit. -->
    <div class="flex min-h-screen bg-valence-grey-50">
        <aside class="w-60 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col px-3 py-4">
            <button
                type="button"
                aria-label="Back to playground"
                class="self-start flex items-center gap-1 rounded-full bg-white hover:bg-gray-50 px-3 py-1.5 shadow-sm border border-gray-200 text-valence-grey-750"
                data-testid="workiq-data-back-button"
                @click="handleGoBack">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                <span class="body-small">Playground</span>
            </button>

            <div class="flex items-center gap-2 px-3 pt-5 pb-3">
                <i class="bi bi-database-add text-[#0078D4]" aria-hidden="true"></i>
                <span class="body-bold text-valence-grey-900">Data tools</span>
                <span class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600">Staging</span>
            </div>

            <nav class="flex flex-col gap-0.5">
                <button
                    v-for="tab in TABS"
                    :key="tab.id"
                    type="button"
                    :data-testid="`workiq-data-tab-${tab.id}`"
                    :aria-current="activeTab === tab.id ? 'page' : undefined"
                    class="flex items-center gap-2.5 rounded-lg px-3 py-2 body-small text-left transition-colors"
                    :class="activeTab === tab.id ? 'bg-[#F0F4FF] text-[#0078D4] font-semibold' : 'text-valence-grey-750 hover:bg-valence-grey-50'"
                    @click="selectTab(tab.id)">
                    <i :class="tab.icon" aria-hidden="true"></i>
                    <span>{{ tab.label }}</span>
                </button>
            </nav>
        </aside>

        <main class="flex-1 overflow-y-auto">
            <div class="mx-auto max-w-3xl flex flex-col gap-4 px-5 md:px-16 py-10">
                <BaseNotification v-if="errorBanner" :theme="'error'" :text="errorBanner" @dismiss="errorBanner = null" />
                <BaseNotification v-if="noticeBanner" :theme="'success'" :text="noticeBanner" @dismiss="noticeBanner = null" />

                <BaseCard>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                            <label for="sandbox-tenant" class="flex flex-col gap-1 text-sm font-semibold">
                                Sandbox tenant
                                <select
                                    id="sandbox-tenant"
                                    v-model="selectedTenant"
                                    class="min-w-[18rem] rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none">
                                    <option v-for="tenant in props.workiq_data_allowed_tenants" :key="tenant" :value="tenant">{{ tenant }}</option>
                                </select>
                            </label>
                            <p class="body-small text-valence-grey-750 md:max-w-md">
                                The connect flow and calendar attendee remapping use the selected sandbox. Team chats only surface counterparties from that same tenant.
                            </p>
                        </div>

                        <div class="flex flex-col gap-2">
                            <div class="text-sm font-semibold text-valence-grey-900">Tenant admin install URL</div>
                            <div class="text-sm text-valence-grey-750">
                                Share this with the sandbox tenant admin to approve the ValenceTeams-hosted data tool app before users connect.
                            </div>
                            <input
                                :value="adminConsentUrl"
                                type="text"
                                readonly
                                aria-label="Tenant admin install URL"
                                class="rounded-lg border-2 border-[#E8E8E8] bg-valence-grey-50 px-3 py-2 text-sm text-valence-grey-900 focus:border-valence-blue focus:outline-none" />
                            <div class="flex items-center gap-3">
                                <BaseButton theme="secondary" :disabled="!adminConsentUrl" @click="copyAdminConsentUrl"> Copy install URL </BaseButton>
                                <a
                                    v-if="adminConsentUrl"
                                    :href="adminConsentUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-sm font-semibold text-valence-blue hover:underline">
                                    Open install URL
                                </a>
                            </div>
                        </div>
                    </div>
                </BaseCard>

                <!-- CALENDAR SYNC -->
                <div v-show="activeTab === 'calendar'" class="flex flex-col gap-4">
                    <BasePageTitle title="Calendar sync" />
                    <p class="body-small text-valence-grey-750 -mt-2">
                        Mirror your Google calendar events (past week + week ahead) into your Outlook calendar on
                        <code>{{ activeTenantLabel }}</code
                        >. Staging only.
                    </p>

                    <BaseCard>
                        <div class="flex flex-col gap-4">
                            <div class="flex items-center justify-between gap-4">
                                <div>
                                    <div class="font-semibold">Google calendar (source)</div>
                                    <div class="text-sm text-gray-600">
                                        {{ statusData?.google_connected ? "Connected" : "Not connected — connect it in Nadia first" }}
                                    </div>
                                </div>
                                <i :class="['bi text-xl', statusData?.google_connected ? 'bi-check-circle-fill text-green-600' : 'bi-x-circle text-gray-400']" aria-hidden="true" />
                            </div>

                            <hr />

                            <div class="flex items-center justify-between gap-4">
                                <div>
                                    <div class="font-semibold">Outlook calendar (destination)</div>
                                    <div class="text-sm text-gray-600">
                                        {{ statusData?.ms_connected ? `Connected as ${statusData.ms_account}` : "Not connected" }}
                                    </div>
                                </div>
                                <div class="flex gap-2">
                                    <BaseButton v-if="!statusData?.ms_connected" theme="primary" @click="connectAccount">Connect MS account</BaseButton>
                                    <BaseButton v-else theme="secondary" :disabled="isDisconnecting" @click="disconnectAccount">Disconnect</BaseButton>
                                </div>
                            </div>
                        </div>
                    </BaseCard>

                    <div class="flex flex-col gap-3">
                        <div class="flex items-center gap-3">
                            <BaseButton theme="primary" :disabled="!canSync || isSyncing" @click="runSync">
                                {{ isSyncing ? "Syncing…" : "Sync past week + week ahead" }}
                            </BaseButton>
                            <span v-if="!canSync" class="text-sm text-gray-500">Connect both calendars to enable sync.</span>
                        </div>
                        <label for="force-recreate" class="flex items-center gap-2 text-sm text-gray-700">
                            <input id="force-recreate" v-model="forceRecreate" type="checkbox" class="rounded border-gray-300" />
                            Force re-create — delete &amp; recreate events that already exist (use when a changed Google event errors with a duplicate id)
                        </label>
                    </div>

                    <BaseCard v-if="syncData">
                        <div class="font-semibold">
                            Synced {{ syncData.synced }} / {{ syncData.total }} events
                            <span v-if="syncData.failed" class="text-red-600">({{ syncData.failed }} failed)</span>
                        </div>
                        <ul class="mt-3 divide-y text-sm">
                            <li v-for="(r, i) in syncData.results" :key="i" class="flex items-start justify-between gap-3 py-2">
                                <div>
                                    <div>{{ r.subject }}</div>
                                    <div class="text-xs text-gray-500">{{ r.start }}</div>
                                    <div v-if="r.detail" class="text-xs text-red-600">{{ r.detail }}</div>
                                </div>
                                <span :class="['shrink-0 text-xs', r.status === 'synced' ? 'text-green-600' : 'text-red-600']">{{ r.status }}</span>
                            </li>
                        </ul>
                    </BaseCard>
                </div>

                <!-- TEAM CHATS -->
                <div v-show="activeTab === 'chat'" class="flex flex-col gap-4">
                    <BasePageTitle title="Team chats" />
                    <p class="body-small text-valence-grey-750 -mt-2">
                        Create a <strong>real 1:1 Teams chat</strong> between you and another connected user. An LLM drafts the back-and-forth; review and edit the messages per
                        user, then confirm to post. Each side is posted with that user's own token, so both must have connected their MS account. (Messages are actually delivered
                        to both Teams clients.)
                    </p>

                    <BaseCard>
                        <div class="flex items-center justify-between gap-4">
                            <div>
                                <div class="font-semibold">Your MS connection</div>
                                <div class="text-sm text-gray-600">
                                    {{ statusData?.ms_connected ? `Connected as ${statusData.ms_account}` : "Not connected" }}
                                </div>
                            </div>
                            <div class="flex gap-2">
                                <BaseButton v-if="!statusData?.ms_connected" theme="primary" @click="connectAccount">Connect MS account</BaseButton>
                                <BaseButton v-else theme="secondary" :disabled="isDisconnecting" @click="disconnectAccount">Disconnect</BaseButton>
                            </div>
                        </div>
                    </BaseCard>

                    <BaseCard>
                        <div class="flex flex-col gap-3">
                            <label for="chat-counterparty" class="flex flex-col gap-1 text-sm font-semibold">
                                Counterparty (other connected users)
                                <select
                                    id="chat-counterparty"
                                    v-model="selectedCounterparty"
                                    :disabled="!statusData?.ms_connected || isLoadingUsers"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none">
                                    <option value="">{{ counterpartyPlaceholder }}</option>
                                    <option v-for="u in chatUsers" :key="u.uuid" :value="u.uuid">{{ u.name }}</option>
                                </select>
                            </label>
                            <label for="chat-topic" class="flex flex-col gap-1 text-sm font-semibold">
                                Topic
                                <input
                                    id="chat-topic"
                                    v-model="topic"
                                    type="text"
                                    placeholder="e.g. Q3 launch timeline slipping"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="chat-description" class="flex flex-col gap-1 text-sm font-semibold">
                                Description / guidance (optional)
                                <textarea
                                    id="chat-description"
                                    v-model="chatDescription"
                                    rows="3"
                                    placeholder="Steer how the conversation goes — e.g. 'They flag a blocker and you agree to pair on it tomorrow.'"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="chat-turns" class="flex flex-col gap-1 text-sm font-semibold">
                                Number of messages (2–10)
                                <input
                                    id="chat-turns"
                                    v-model.number="turns"
                                    type="number"
                                    min="2"
                                    max="10"
                                    class="w-24 rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="chat-cp-starts" class="flex items-center gap-2 text-sm text-gray-700">
                                <input id="chat-cp-starts" v-model="threadCounterpartyStarts" type="checkbox" class="rounded border-gray-300" />
                                Counterparty sends the first message
                            </label>
                            <div class="flex items-center gap-3">
                                <BaseButton theme="primary" :disabled="!canGenerate || isPreviewing" @click="runPreview">
                                    {{ isPreviewing ? "Generating…" : draftMessages.length ? "Regenerate draft ✨" : "Generate draft ✨" }}
                                </BaseButton>
                                <span v-if="!canGenerate" class="text-sm text-gray-500">Connect your MS account, pick a counterparty, and enter a topic.</span>
                            </div>
                        </div>
                    </BaseCard>

                    <!-- Step 2: review & edit the drafted messages per user, then confirm to post. -->
                    <BaseCard v-if="draftMessages.length">
                        <div class="flex items-center justify-between gap-3">
                            <div class="font-semibold">Review &amp; edit — {{ draftMessages.length }} message{{ draftMessages.length === 1 ? "" : "s" }}</div>
                            <span class="text-xs text-gray-500">Nothing is posted until you confirm.</span>
                        </div>
                        <div class="mt-3 flex flex-col gap-3">
                            <div
                                v-for="(message, i) in draftMessages"
                                :key="i"
                                class="flex flex-col gap-1 rounded-lg border-2 p-3"
                                :class="message.sender === 'me' ? 'border-[#DCE6FF] bg-[#F5F8FF]' : 'border-[#E8E8E8] bg-white'">
                                <div class="flex items-center justify-between gap-2">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-valence-grey-600">
                                        {{ message.sender_label }}
                                        <span v-if="message.sender === 'me'" class="ml-1 text-valence-blue">(you)</span>
                                    </span>
                                    <button type="button" class="text-xs text-red-600 hover:underline" :aria-label="`Remove message ${i + 1}`" @click="removeDraftMessage(i)">
                                        Remove
                                    </button>
                                </div>
                                <textarea
                                    v-model="message.text"
                                    rows="2"
                                    :data-testid="`chat-draft-msg-${i}`"
                                    :aria-label="`Message ${i + 1} from ${message.sender_label}`"
                                    class="w-full rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm focus:border-valence-blue focus:outline-none" />
                            </div>
                        </div>
                        <div class="mt-4 flex items-center gap-3">
                            <BaseButton theme="primary" :disabled="!canPost || isPosting" @click="runPost">
                                {{ isPosting ? "Posting…" : "Post to Teams 💬" }}
                            </BaseButton>
                            <BaseButton theme="secondary" :disabled="isPosting" @click="discardDraft">Discard</BaseButton>
                        </div>
                    </BaseCard>

                    <BaseCard v-if="threadData">
                        <div class="font-semibold">Posted {{ threadData.posted }} / {{ threadData.total }} messages</div>
                        <ul class="mt-3 divide-y text-sm">
                            <li v-for="(r, i) in threadData.results" :key="i" class="flex items-start justify-between gap-3 py-2">
                                <div>
                                    <div>{{ r.from }} → {{ r.to }}</div>
                                    <div v-if="r.detail" class="text-xs text-red-600">{{ r.detail }}</div>
                                </div>
                                <span :class="['shrink-0 text-xs', r.status === 'posted' ? 'text-green-600' : 'text-red-600']">{{ r.status }}</span>
                            </li>
                        </ul>
                    </BaseCard>
                </div>

                <!-- SEED MY MAILBOX -->
                <div v-show="activeTab === 'seed'" class="flex flex-col gap-4">
                    <BasePageTitle title="Seed mailbox" />
                    <p class="body-small text-valence-grey-750 -mt-2">
                        Plant an LLM-drafted thread directly into <strong>one</strong> connected mailbox — <strong>nothing is sent</strong>. Review and edit the subject and
                        messages per user, then confirm to plant. The counterparty's messages land in Inbox, yours in Sent Items. The counterparty can be any address (even
                        external), since no mail leaves.
                    </p>

                    <BaseCard>
                        <div class="flex items-center justify-between gap-4">
                            <div>
                                <div class="font-semibold">Mailbox to seed</div>
                                <div class="text-sm text-gray-600">
                                    {{ statusData?.ms_connected ? `Connected as ${statusData.ms_account}` : "Not connected" }}
                                </div>
                            </div>
                            <div class="flex gap-2">
                                <BaseButton v-if="!statusData?.ms_connected" theme="primary" @click="connectAccount">Connect MS account</BaseButton>
                                <BaseButton v-else theme="secondary" :disabled="isDisconnecting" @click="disconnectAccount">Disconnect</BaseButton>
                            </div>
                        </div>
                    </BaseCard>

                    <BaseCard>
                        <div class="flex flex-col gap-3">
                            <label for="seed-topic" class="flex flex-col gap-1 text-sm font-semibold">
                                Topic
                                <input
                                    id="seed-topic"
                                    v-model="seedTopic"
                                    type="text"
                                    placeholder="e.g. Reschedule the Q3 planning offsite"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="seed-description" class="flex flex-col gap-1 text-sm font-semibold">
                                Description / guidance (optional)
                                <textarea
                                    id="seed-description"
                                    v-model="seedDescription"
                                    rows="3"
                                    placeholder="Steer how the conversation goes — e.g. 'Jordan is frustrated about the slip and pushes for a firm date; you stay calm and propose two options.'"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="seed-cp-name" class="flex flex-col gap-1 text-sm font-semibold">
                                Counterparty name
                                <input
                                    id="seed-cp-name"
                                    v-model="counterpartyName"
                                    type="text"
                                    placeholder="e.g. Jordan Lee"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="seed-cp-email" class="flex flex-col gap-1 text-sm font-semibold">
                                Counterparty email
                                <input
                                    id="seed-cp-email"
                                    v-model="counterpartyEmail"
                                    type="email"
                                    placeholder="e.g. jordan@example.com"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="seed-turns" class="flex flex-col gap-1 text-sm font-semibold">
                                Number of messages (2–10)
                                <input
                                    id="seed-turns"
                                    v-model.number="seedTurns"
                                    type="number"
                                    min="2"
                                    max="10"
                                    class="w-24 rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="seed-cp-starts" class="flex items-center gap-2 text-sm text-gray-700">
                                <input id="seed-cp-starts" v-model="counterpartyStarts" type="checkbox" class="rounded border-gray-300" />
                                Counterparty sends the first message
                            </label>
                            <div class="flex items-center gap-3">
                                <BaseButton theme="primary" :disabled="!canSeed || isSeedPreviewing" @click="runSeedPreview">
                                    {{ isSeedPreviewing ? "Generating…" : seedDraftMessages.length ? "Regenerate draft ✨" : "Generate draft ✨" }}
                                </BaseButton>
                                <span v-if="!canSeed" class="text-sm text-gray-500">Connect your MS account, and enter a topic + counterparty email.</span>
                            </div>
                        </div>
                    </BaseCard>

                    <!-- Step 2: review & edit the drafted subject + messages per user, then confirm to plant. -->
                    <BaseCard v-if="seedDraftMessages.length">
                        <div class="flex items-center justify-between gap-3">
                            <div class="font-semibold">Review &amp; edit — {{ seedDraftMessages.length }} message{{ seedDraftMessages.length === 1 ? "" : "s" }}</div>
                            <span class="text-xs text-gray-500">Nothing is planted until you confirm.</span>
                        </div>
                        <label for="seed-subject" class="mt-3 flex flex-col gap-1 text-sm font-semibold">
                            Subject
                            <input
                                id="seed-subject"
                                v-model="seedSubject"
                                type="text"
                                class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                        </label>
                        <div class="mt-3 flex flex-col gap-3">
                            <div
                                v-for="(message, i) in seedDraftMessages"
                                :key="i"
                                class="flex flex-col gap-1 rounded-lg border-2 p-3"
                                :class="message.sender === 'me' ? 'border-[#DCE6FF] bg-[#F5F8FF]' : 'border-[#E8E8E8] bg-white'">
                                <div class="flex items-center justify-between gap-2">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-valence-grey-600">
                                        {{ message.sender_label }}
                                        <span v-if="message.sender === 'me'" class="ml-1 text-valence-blue">(you · Sent Items)</span>
                                        <span v-else class="ml-1 text-valence-grey-500">(Inbox)</span>
                                    </span>
                                    <button type="button" class="text-xs text-red-600 hover:underline" :aria-label="`Remove message ${i + 1}`" @click="removeSeedMessage(i)">
                                        Remove
                                    </button>
                                </div>
                                <textarea
                                    v-model="message.text"
                                    rows="3"
                                    :data-testid="`seed-draft-msg-${i}`"
                                    :aria-label="`Message ${i + 1} from ${message.sender_label}`"
                                    class="w-full rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm focus:border-valence-blue focus:outline-none" />
                            </div>
                        </div>
                        <div class="mt-4 flex items-center gap-3">
                            <BaseButton theme="primary" :disabled="!canPlant || isSeeding" @click="runSeed">
                                {{ isSeeding ? "Planting…" : "Plant seeds 🌱" }}
                            </BaseButton>
                            <BaseButton theme="secondary" :disabled="isSeeding" @click="discardSeedDraft">Discard</BaseButton>
                        </div>
                    </BaseCard>

                    <BaseCard v-if="seedData">
                        <div class="font-semibold">{{ seedData.subject }} — planted {{ seedData.planted }} / {{ seedData.total }}</div>
                        <ul class="mt-3 divide-y text-sm">
                            <li v-for="(r, i) in seedData.results" :key="i" class="flex items-start justify-between gap-3 py-2">
                                <div>
                                    <div>
                                        {{ r.from }} → {{ r.to }} <span class="text-xs text-gray-500">({{ r.folder }})</span>
                                    </div>
                                    <div v-if="r.detail" class="text-xs text-red-600">{{ r.detail }}</div>
                                </div>
                                <span :class="['shrink-0 text-xs', r.status === 'planted' ? 'text-green-600' : 'text-red-600']">{{ r.status }}</span>
                            </li>
                        </ul>
                    </BaseCard>
                </div>

                <!-- MEETING TRANSCRIPTS -->
                <div v-show="activeTab === 'transcript'" class="flex flex-col gap-4">
                    <BasePageTitle title="Meeting transcripts" />
                    <p class="body-small text-valence-grey-750 -mt-2">
                        Generate a synthetic Teams meeting transcript and upload it as a <strong>.docx</strong> to your connected OneDrive, under
                        <code>My files &gt; Recordings/</code> — the same folder real Teams meeting transcripts land in. Review and edit the turns, then confirm to upload.
                    </p>

                    <BaseCard>
                        <div class="flex items-center justify-between gap-4">
                            <div>
                                <div class="font-semibold">OneDrive to upload to</div>
                                <div class="text-sm text-gray-600">
                                    {{ statusData?.ms_connected ? `Connected as ${statusData.ms_account}` : "Not connected" }}
                                </div>
                            </div>
                            <div class="flex gap-2">
                                <BaseButton v-if="!statusData?.ms_connected" theme="primary" @click="connectAccount">Connect MS account</BaseButton>
                                <BaseButton v-else theme="secondary" :disabled="isDisconnecting" @click="disconnectAccount">Disconnect</BaseButton>
                            </div>
                        </div>
                    </BaseCard>

                    <BaseCard>
                        <div class="flex flex-col gap-3">
                            <label for="transcript-topic" class="flex flex-col gap-1 text-sm font-semibold">
                                Topic
                                <input
                                    id="transcript-topic"
                                    v-model="transcriptTopic"
                                    type="text"
                                    placeholder="e.g. Weekly project status sync"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <label for="transcript-description" class="flex flex-col gap-1 text-sm font-semibold">
                                Description / guidance (optional)
                                <textarea
                                    id="transcript-description"
                                    v-model="transcriptDescription"
                                    rows="3"
                                    placeholder="Steer the meeting -- e.g. 'This is a manager's 1:1 with a direct report; they discuss a stalled project and agree on next steps.'"
                                    class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <div class="flex flex-col gap-1 text-sm font-semibold">
                                Participants (2 for a 1:1, 3+ for a group meeting)
                                <div v-for="(_p, i) in transcriptParticipants" :key="i" class="flex items-center gap-2">
                                    <input
                                        v-model="transcriptParticipants[i]"
                                        type="text"
                                        :placeholder="`Participant ${i + 1} name`"
                                        :aria-label="`Participant ${i + 1} name`"
                                        :data-testid="`transcript-participant-${i}`"
                                        class="w-full rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                                    <button
                                        v-if="transcriptParticipants.length > 2"
                                        type="button"
                                        class="text-xs text-red-600 hover:underline"
                                        :aria-label="`Remove participant ${i + 1}`"
                                        @click="removeTranscriptParticipant(i)">
                                        Remove
                                    </button>
                                </div>
                                <button type="button" class="self-start text-sm font-semibold text-valence-blue hover:underline" @click="addTranscriptParticipant">
                                    + Add participant
                                </button>
                            </div>
                            <label for="transcript-turns" class="flex flex-col gap-1 text-sm font-semibold">
                                Number of turns (4–40)
                                <input
                                    id="transcript-turns"
                                    v-model.number="transcriptTurns"
                                    type="number"
                                    min="4"
                                    max="40"
                                    class="w-24 rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                            </label>
                            <div class="flex items-center gap-3">
                                <BaseButton theme="primary" :disabled="!canGenerateTranscript || isTranscriptPreviewing" @click="runTranscriptPreview">
                                    {{ isTranscriptPreviewing ? "Generating…" : transcriptDraftTurns.length ? "Regenerate draft ✨" : "Generate draft ✨" }}
                                </BaseButton>
                                <span v-if="!canGenerateTranscript" class="text-sm text-gray-500">Connect your MS account, enter a topic, and name at least two participants.</span>
                            </div>
                        </div>
                    </BaseCard>

                    <!-- Step 2: review & edit the drafted turns, then confirm to upload. -->
                    <BaseCard v-if="transcriptDraftTurns.length">
                        <div class="flex items-center justify-between gap-3">
                            <div class="font-semibold">Review &amp; edit — {{ transcriptDraftTurns.length }} turn{{ transcriptDraftTurns.length === 1 ? "" : "s" }}</div>
                            <span class="text-xs text-gray-500">Nothing is uploaded until you confirm.</span>
                        </div>
                        <label for="transcript-title" class="mt-3 flex flex-col gap-1 text-sm font-semibold">
                            Meeting title
                            <input
                                id="transcript-title"
                                v-model="transcriptTitle"
                                type="text"
                                class="rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm font-normal focus:border-valence-blue focus:outline-none" />
                        </label>
                        <div class="mt-3 flex flex-col gap-3">
                            <div v-for="(turn, i) in transcriptDraftTurns" :key="i" class="flex flex-col gap-1 rounded-lg border-2 border-[#E8E8E8] bg-white p-3">
                                <div class="flex items-center justify-between gap-2">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-valence-grey-600">{{ turn.speaker }}</span>
                                    <button type="button" class="text-xs text-red-600 hover:underline" :aria-label="`Remove turn ${i + 1}`" @click="removeTranscriptTurn(i)">
                                        Remove
                                    </button>
                                </div>
                                <textarea
                                    v-model="turn.text"
                                    rows="2"
                                    :data-testid="`transcript-draft-turn-${i}`"
                                    :aria-label="`Turn ${i + 1} from ${turn.speaker}`"
                                    class="w-full rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 text-sm focus:border-valence-blue focus:outline-none" />
                            </div>
                        </div>
                        <div class="mt-4 flex items-center gap-3">
                            <BaseButton theme="primary" :disabled="!canUploadTranscript || isUploadingTranscript" @click="runTranscriptUpload">
                                {{ isUploadingTranscript ? "Uploading…" : "Upload to OneDrive ☁️" }}
                            </BaseButton>
                            <BaseButton theme="secondary" :disabled="isUploadingTranscript" @click="discardTranscriptDraft">Discard</BaseButton>
                        </div>
                    </BaseCard>

                    <BaseCard v-if="transcriptResult">
                        <div class="font-semibold">Uploaded {{ transcriptResult.filename }}</div>
                        <div class="text-sm text-gray-600">To {{ transcriptResult.me_email }}'s OneDrive.</div>
                        <a
                            v-if="transcriptResult.web_url"
                            :href="transcriptResult.web_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-2 inline-block text-sm font-semibold text-valence-blue hover:underline">
                            Open in OneDrive
                        </a>
                    </BaseCard>
                </div>
            </div>
        </main>
    </div>
</template>

<script lang="ts">
// Staging-only data-creation tools for the Work IQ playground, reached from the playground
// sidebar. Rendered standalone (no main-app layout) with its own inline sidebar so the whole
// preview tool can be deleted as a single unit. See workiq/dev_data* on the backend.
export interface StatusResponse {
    // A single connected MS account powers every tool, so one flag gates all of them.
    ms_connected: boolean;
    ms_account: string;
    ms_target_domain: string;
    google_connected: boolean;
}

// Counterparty for the Teams-chat tool: another user who has connected the data-gen tool.
export interface ChatUser {
    uuid: string;
    name: string;
}

// One drafted message from the preview step, editable before posting.
export interface DraftMessage {
    sender: "me" | "counterparty";
    sender_label: string;
    text: string;
}

export interface PreviewResponse {
    messages: DraftMessage[];
    counterparty_uuid: string;
    me_label: string;
    counterparty_label: string;
    error?: string;
}

export interface ChatResult {
    from: string;
    to: string;
    status: string;
    detail: string | null;
}

export interface ChatResponse {
    chat_id: string;
    posted: number;
    total: number;
    results: ChatResult[];
    error?: string;
}

export interface SeedResult {
    from: string;
    to: string;
    folder: string;
    status: string;
    detail: string | null;
}

export interface SeedResponse {
    subject: string;
    planted: number;
    total: number;
    results: SeedResult[];
    error?: string;
}

export interface SeedPreviewResponse {
    subject: string;
    messages: DraftMessage[];
    me_email: string;
    me_name: string;
    counterparty_email: string;
    counterparty_name: string;
    error?: string;
}

export interface SyncResult {
    subject: string;
    start: string | null;
    status: string;
    detail: string | null;
}

export interface SyncResponse {
    total: number;
    synced: number;
    failed: number;
    results: SyncResult[];
    error?: string;
}

// One drafted turn from the meeting-transcript preview step, editable before upload.
export interface DraftTurn {
    speaker: string;
    text: string;
}

export interface TranscriptPreviewResponse {
    title: string;
    participants: string[];
    turns: DraftTurn[];
    error?: string;
}

export interface TranscriptUploadResponse {
    filename: string;
    web_url: string | null;
    me_email: string;
    error?: string;
}
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import { computed, onMounted, ref, watch } from "vue";

const props = withDefaults(
    defineProps<{
        initial_tab?: string;
        workiq_data_base_url?: string;
        workiq_playground_url?: string;
        workiq_status_url?: string;
        workiq_login_url?: string;
        workiq_disconnect_url?: string;
        workiq_sync_calendar_url?: string;
        workiq_chat_users_url?: string;
        workiq_preview_team_chat_url?: string;
        workiq_generate_team_chat_url?: string;
        workiq_preview_seed_mailbox_url?: string;
        workiq_seed_mailbox_url?: string;
        workiq_preview_meeting_transcript_url?: string;
        workiq_generate_meeting_transcript_url?: string;
        workiq_admin_consent_url?: string;
        workiq_data_allowed_tenants?: string[];
        workiq_data_default_tenant?: string;
    }>(),
    {
        initial_tab: "calendar",
        workiq_data_base_url: "/admin/workiq/playground/data/",
        workiq_playground_url: "/admin/workiq/playground/",
        workiq_status_url: "/workiq/dev-data/status",
        workiq_login_url: "/workiq/dev-data/login",
        workiq_disconnect_url: "/workiq/dev-data/disconnect",
        workiq_sync_calendar_url: "/workiq/dev-data/sync-calendar",
        workiq_chat_users_url: "/workiq/dev-data/chat-users",
        workiq_preview_team_chat_url: "/workiq/dev-data/preview-team-chat",
        workiq_generate_team_chat_url: "/workiq/dev-data/generate-team-chat",
        workiq_preview_seed_mailbox_url: "/workiq/dev-data/preview-seed-mailbox",
        workiq_seed_mailbox_url: "/workiq/dev-data/seed-mailbox",
        workiq_preview_meeting_transcript_url: "/workiq/dev-data/preview-meeting-transcript",
        workiq_generate_meeting_transcript_url: "/workiq/dev-data/generate-meeting-transcript",
        workiq_admin_consent_url: "/workiq/dev-data/admin-consent-url",
        workiq_data_allowed_tenants: () => ["ValenceTeams.onmicrosoft.com"],
        workiq_data_default_tenant: "ValenceTeams.onmicrosoft.com",
    },
);

const TABS = [
    { id: "calendar", label: "Calendar sync", icon: "bi bi-calendar2-week" },
    { id: "chat", label: "Team chats", icon: "bi bi-chat-dots" },
    { id: "seed", label: "Seed mailbox", icon: "bi bi-inbox" },
    { id: "transcript", label: "Meeting transcripts", icon: "bi bi-file-earmark-text" },
] as const;
const VALID_TABS = TABS.map((tab) => tab.id) as readonly string[];
const activeTab = ref(VALID_TABS.includes(props.initial_tab) ? props.initial_tab : "calendar");
const selectedTenant = ref(props.workiq_data_default_tenant);
const adminConsentUrl = ref("");

// Reflect the active tab in the URL path (…/data/<tab>/) so a refresh stays put. Done with
// replaceState rather than Inertia navigation to avoid a server round-trip per tab click;
// the Django route serves the same page for any sub-path on refresh.
function selectTab(tab: string) {
    activeTab.value = tab;
    globalThis.history.replaceState(globalThis.history.state, "", `${props.workiq_data_base_url}${tab}/`);
}

function handleGoBack() {
    // Always go straight to the playground — history.back() can land on the OAuth callback
    // page after a connect flow.
    router.visit(props.workiq_playground_url);
}

const errorBanner = ref<string | null>(null);
const noticeBanner = ref<string | null>(null);
const statusData = ref<StatusResponse | null>(null);

const canSync = computed(() => !!statusData.value?.ms_connected && !!statusData.value?.google_connected);

const { data: statusFetch, execute: fetchStatus } = useFetch(props.workiq_status_url, { immediate: false }).get().json<StatusResponse>();

const refreshStatus = async () => {
    await fetchStatus();
    if (statusFetch.value) {
        statusData.value = statusFetch.value;
        if (statusFetch.value.ms_target_domain) {
            selectedTenant.value = statusFetch.value.ms_target_domain;
        }
    }
    // The Teams-chat counterparty picker lists other connected users.
    if (statusData.value?.ms_connected) {
        loadChatUsers();
    } else {
        chatUsers.value = [];
    }
};

const loadAdminConsentUrl = async () => {
    const url = new URL(props.workiq_admin_consent_url, globalThis.location.origin);
    url.searchParams.set("tenant", selectedTenant.value);
    const { data } = await useFetch(url.toString()).get().json<{ admin_consent_url?: string }>();
    adminConsentUrl.value = data.value?.admin_consent_url || "";
};

onMounted(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const err = params.get("error");
    const notice = params.get("notice");
    if (err) {
        errorBanner.value = `Outlook connection failed: ${err}`;
    }
    if (notice) {
        noticeBanner.value = notice;
    }
    refreshStatus();
    loadAdminConsentUrl();
});

// A single MS account powers every tool, so connect/disconnect are shared across all tabs.
const connectAccount = () => {
    const url = new URL(props.workiq_login_url, globalThis.location.origin);
    url.searchParams.set("tenant", selectedTenant.value);
    globalThis.location.href = url.toString();
};

const copyAdminConsentUrl = async () => {
    if (!adminConsentUrl.value) {
        return;
    }
    await globalThis.navigator.clipboard.writeText(adminConsentUrl.value);
    noticeBanner.value = "Tenant admin install URL copied.";
};

const activeTenantLabel = computed(() => statusData.value?.ms_target_domain || selectedTenant.value);

watch(selectedTenant, () => {
    loadAdminConsentUrl();
});

const isDisconnecting = ref(false);
const disconnectAccount = async () => {
    isDisconnecting.value = true;
    await useFetch(props.workiq_disconnect_url).post().json();
    await refreshStatus();
    isDisconnecting.value = false;
};

const isSyncing = ref(false);
const forceRecreate = ref(false);
const syncData = ref<SyncResponse | null>(null);

const runSync = async () => {
    isSyncing.value = true;
    errorBanner.value = null;
    syncData.value = null;
    const { data: syncFetch, error: syncError } = await useFetch(props.workiq_sync_calendar_url).post({ force: forceRecreate.value }).json<SyncResponse>();
    if (syncError.value) {
        errorBanner.value = syncFetch.value?.error || "Sync request failed.";
    } else if (syncFetch.value?.error) {
        errorBanner.value = syncFetch.value.error;
    } else if (syncFetch.value) {
        syncData.value = syncFetch.value;
    }
    isSyncing.value = false;
};

// --- Team chats (real 1:1 Teams chat between two Work-IQ-connected users) ---
const chatUsers = ref<ChatUser[]>([]);
const isLoadingUsers = ref(false);
const selectedCounterparty = ref("");
const counterpartyPlaceholder = computed(() => (isLoadingUsers.value ? "Loading users…" : chatUsers.value.length ? "Select a user…" : "No other connected users yet"));

const loadChatUsers = async () => {
    isLoadingUsers.value = true;
    const { data } = await useFetch(props.workiq_chat_users_url).get().json<{ users?: ChatUser[]; error?: string }>();
    chatUsers.value = data.value?.users || [];
    isLoadingUsers.value = false;
};

const topic = ref("");
const chatDescription = ref("");
const turns = ref(4);
const threadCounterpartyStarts = ref(true);
const isPreviewing = ref(false);
const isPosting = ref(false);
const threadData = ref<ChatResponse | null>(null);
// The generated-but-not-yet-posted draft the operator reviews/edits before confirming.
const draftMessages = ref<DraftMessage[]>([]);

const canGenerate = computed(() => !!statusData.value?.ms_connected && !!selectedCounterparty.value && topic.value.trim().length > 0);
const canPost = computed(() => draftMessages.value.some((m) => m.text.trim().length > 0));

// Step 1 — generate the thread and hold it for review (nothing is posted yet).
const runPreview = async () => {
    isPreviewing.value = true;
    errorBanner.value = null;
    threadData.value = null;
    draftMessages.value = [];
    const { data: previewFetch, error: previewError } = await useFetch(props.workiq_preview_team_chat_url)
        .post({
            topic: topic.value,
            description: chatDescription.value,
            turns: turns.value,
            counterparty_uuid: selectedCounterparty.value,
            counterparty_starts: threadCounterpartyStarts.value,
        })
        .json<PreviewResponse>();
    if (previewError.value) {
        errorBanner.value = previewFetch.value?.error || "Chat generation failed.";
    } else if (previewFetch.value?.error) {
        errorBanner.value = previewFetch.value.error;
    } else if (previewFetch.value) {
        draftMessages.value = previewFetch.value.messages || [];
    }
    isPreviewing.value = false;
};

const removeDraftMessage = (index: number) => {
    draftMessages.value.splice(index, 1);
};

const discardDraft = () => {
    draftMessages.value = [];
};

// Step 2 — create the real chat and post the reviewed/edited messages.
const runPost = async () => {
    isPosting.value = true;
    errorBanner.value = null;
    threadData.value = null;
    const { data: threadFetch, error: threadError } = await useFetch(props.workiq_generate_team_chat_url)
        .post({
            counterparty_uuid: selectedCounterparty.value,
            messages: draftMessages.value.map((m) => ({ sender: m.sender, text: m.text })),
        })
        .json<ChatResponse>();
    if (threadError.value) {
        errorBanner.value = threadFetch.value?.error || "Chat creation failed.";
    } else if (threadFetch.value?.error) {
        errorBanner.value = threadFetch.value.error;
    } else if (threadFetch.value) {
        threadData.value = threadFetch.value;
        draftMessages.value = [];
    }
    isPosting.value = false;
};

// Changing the counterparty invalidates a draft generated for the previous person.
watch(selectedCounterparty, () => {
    draftMessages.value = [];
    threadData.value = null;
});

// --- Seed my mailbox (single user) ---
const seedTopic = ref("");
const seedDescription = ref("");
const seedTurns = ref(4);
const counterpartyName = ref("");
const counterpartyEmail = ref("");
const counterpartyStarts = ref(true);
const isSeedPreviewing = ref(false);
const isSeeding = ref(false);
const seedData = ref<SeedResponse | null>(null);
// The generated-but-not-yet-planted thread the operator reviews/edits before confirming.
const seedSubject = ref("");
const seedDraftMessages = ref<DraftMessage[]>([]);

const canSeed = computed(() => !!statusData.value?.ms_connected && seedTopic.value.trim().length > 0 && counterpartyEmail.value.trim().length > 0);
const canPlant = computed(() => seedDraftMessages.value.some((m) => m.text.trim().length > 0));

// Step 1 — generate the thread and hold it for review (nothing is planted yet).
const runSeedPreview = async () => {
    isSeedPreviewing.value = true;
    errorBanner.value = null;
    seedData.value = null;
    seedDraftMessages.value = [];
    const { data: previewFetch, error: previewError } = await useFetch(props.workiq_preview_seed_mailbox_url)
        .post({
            topic: seedTopic.value,
            description: seedDescription.value,
            turns: seedTurns.value,
            counterparty_name: counterpartyName.value,
            counterparty_email: counterpartyEmail.value,
            counterparty_starts: counterpartyStarts.value,
        })
        .json<SeedPreviewResponse>();
    if (previewError.value) {
        errorBanner.value = previewFetch.value?.error || "Thread generation failed.";
    } else if (previewFetch.value?.error) {
        errorBanner.value = previewFetch.value.error;
    } else if (previewFetch.value) {
        seedSubject.value = previewFetch.value.subject || "";
        seedDraftMessages.value = previewFetch.value.messages || [];
    }
    isSeedPreviewing.value = false;
};

const removeSeedMessage = (index: number) => {
    seedDraftMessages.value.splice(index, 1);
};

const discardSeedDraft = () => {
    seedDraftMessages.value = [];
};

// Step 2 — plant the reviewed/edited thread into the mailbox.
const runSeed = async () => {
    isSeeding.value = true;
    errorBanner.value = null;
    seedData.value = null;
    const { data: seedFetch, error: seedError } = await useFetch(props.workiq_seed_mailbox_url)
        .post({
            subject: seedSubject.value,
            counterparty_name: counterpartyName.value,
            counterparty_email: counterpartyEmail.value,
            messages: seedDraftMessages.value.map((m) => ({ sender: m.sender, text: m.text })),
        })
        .json<SeedResponse>();
    if (seedError.value) {
        errorBanner.value = seedFetch.value?.error || "Seeding failed.";
    } else if (seedFetch.value?.error) {
        errorBanner.value = seedFetch.value.error;
    } else if (seedFetch.value) {
        seedData.value = seedFetch.value;
        seedDraftMessages.value = [];
    }
    isSeeding.value = false;
};

// Changing the counterparty email invalidates a draft generated for the previous address.
watch(counterpartyEmail, () => {
    seedDraftMessages.value = [];
    seedData.value = null;
});

// --- Meeting transcripts (upload a synthetic .docx transcript to the connected OneDrive) ---
const transcriptTopic = ref("");
const transcriptDescription = ref("");
const transcriptParticipants = ref<string[]>(["", ""]);
const transcriptTurns = ref(12);
const isTranscriptPreviewing = ref(false);
const isUploadingTranscript = ref(false);
const transcriptResult = ref<TranscriptUploadResponse | null>(null);
// The generated-but-not-yet-uploaded draft the operator reviews/edits before confirming.
const transcriptTitle = ref("");
const transcriptDraftTurns = ref<DraftTurn[]>([]);

const canGenerateTranscript = computed(
    () => !!statusData.value?.ms_connected && transcriptTopic.value.trim().length > 0 && transcriptParticipants.value.filter((p) => p.trim().length > 0).length >= 2,
);
const canUploadTranscript = computed(() => transcriptDraftTurns.value.some((t) => t.text.trim().length > 0));

const addTranscriptParticipant = () => {
    transcriptParticipants.value.push("");
};

const removeTranscriptParticipant = (index: number) => {
    transcriptParticipants.value.splice(index, 1);
};

// Step 1 — generate the transcript and hold it for review (nothing is uploaded yet).
const runTranscriptPreview = async () => {
    isTranscriptPreviewing.value = true;
    errorBanner.value = null;
    transcriptResult.value = null;
    transcriptDraftTurns.value = [];
    const { data: previewFetch, error: previewError } = await useFetch(props.workiq_preview_meeting_transcript_url)
        .post({
            topic: transcriptTopic.value,
            description: transcriptDescription.value,
            turns: transcriptTurns.value,
            participants: transcriptParticipants.value.map((p) => p.trim()).filter(Boolean),
        })
        .json<TranscriptPreviewResponse>();
    if (previewError.value) {
        errorBanner.value = previewFetch.value?.error || "Transcript generation failed.";
    } else if (previewFetch.value?.error) {
        errorBanner.value = previewFetch.value.error;
    } else if (previewFetch.value) {
        transcriptTitle.value = previewFetch.value.title || "";
        transcriptDraftTurns.value = previewFetch.value.turns || [];
    }
    isTranscriptPreviewing.value = false;
};

const removeTranscriptTurn = (index: number) => {
    transcriptDraftTurns.value.splice(index, 1);
};

const discardTranscriptDraft = () => {
    transcriptDraftTurns.value = [];
};

// Step 2 — render the reviewed/edited transcript as a .docx and upload it to OneDrive.
const runTranscriptUpload = async () => {
    isUploadingTranscript.value = true;
    errorBanner.value = null;
    transcriptResult.value = null;
    const { data: uploadFetch, error: uploadError } = await useFetch(props.workiq_generate_meeting_transcript_url)
        .post({
            title: transcriptTitle.value,
            turns: transcriptDraftTurns.value.map((t) => ({ speaker: t.speaker, text: t.text })),
        })
        .json<TranscriptUploadResponse>();
    if (uploadError.value) {
        errorBanner.value = uploadFetch.value?.error || "Transcript upload failed.";
    } else if (uploadFetch.value?.error) {
        errorBanner.value = uploadFetch.value.error;
    } else if (uploadFetch.value) {
        transcriptResult.value = uploadFetch.value;
        transcriptDraftTurns.value = [];
    }
    isUploadingTranscript.value = false;
};
</script>
